import React, { FC, useState, useRef, useEffect, useCallback } from "react";
import { CellProps } from "react-table";
import SupplyPortal from "../../../../SupplyPortal";

/**
 * ShowMoreWrapper
 *
 * Both "Show More" and "Show Less" are rendered via portal (fixed positioning),
 * fully out of the DOM flow.
 *
 * "Show Less" is positioned by measuring the end of the last text node inside
 * the cell using the Range API (range.getBoundingClientRect()), so it appears
 * to sit naturally after the final word of the paragraph — even as the cell
 * resizes or the text reflows.
 */

interface ShowMoreWrapperProps {
	children: React.ReactNode;
	isExpanded: boolean;
	onToggle: () => void;
	needsExpansion: boolean;
	className?: string;
}

/**
 * Finds the last visible text node inside a container element.
 * Walks the DOM tree depth-first and returns the deepest last text node
 * that has non-whitespace content.
 */
function getLastTextNode(el: Element): Text | null {
	const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
	let last: Text | null = null;
	let node: Node | null;
	while ((node = walker.nextNode())) {
		if ((node as Text).textContent?.trim()) {
			last = node as Text;
		}
	}
	return last;
}

/**
 * Returns the bounding rect of the very last character in a text node.
 * Uses the Range API to measure just the final character's position,
 * which gives us the exact coordinate where the text ends.
 */
function getLastCharRect(textNode: Text): DOMRect | null {
	const len = textNode.textContent?.length ?? 0;
	if (len === 0) return null;
	const range = document.createRange();
	range.setStart(textNode, len - 1);
	range.setEnd(textNode, len);
	return range.getBoundingClientRect();
}

export const ShowMoreWrapper: FC<ShowMoreWrapperProps> = ({
	children,
	isExpanded,
	onToggle,
	needsExpansion,
	className = "",
}) => {
	const [hovered, setHovered] = useState(false);
	const [showMorePos, setShowMorePos] = useState({ left: 0, top: 0 });
	const [showLessPos, setShowLessPos] = useState({ left: 0, top: 0 });

	const cellRef = useRef<HTMLDivElement>(null);
	const hideTimer = useRef<number>();

	const clearHide = () => {
		if (hideTimer.current) window.clearTimeout(hideTimer.current);
	};
	useEffect(() => {
		return () => clearHide();
	}, []);

	/**
	 * Calculates position for "Show Less" by finding the last text node
	 * inside the cell and measuring where it ends via the Range API.
	 *
	 * Falls back to bottom-left of the cell if no text node is found.
	 */
	const measureShowLessPosition = useCallback(() => {
		if (!cellRef.current) return;

		const lastTextNode = getLastTextNode(cellRef.current);
		if (lastTextNode) {
			const rect = getLastCharRect(lastTextNode);
			if (rect) {
				setShowLessPos({
					// Position just after the last character, with a small gap
					left: rect.right + 4,
					// Vertically centered on that last line
					top: rect.top - 2 + rect.height / 2,
				});
				return;
			}
		}

		// Fallback: bottom-left of the cell
		const cellRect = cellRef.current.getBoundingClientRect();
		setShowLessPos({
			left: cellRect.left,
			top: cellRect.bottom,
		});
	}, []);

	/**
	 * Recalculate Show Less position whenever expansion state changes.
	 * rAF ensures we wait for the DOM to finish reflowing after expansion
	 * before we measure — otherwise we'd measure the collapsed layout.
	 */
	useEffect(() => {
		if (isExpanded && needsExpansion) {
			requestAnimationFrame(() => {
				measureShowLessPosition();
			});
		}
	}, [isExpanded, needsExpansion, measureShowLessPosition]);

	const showButton = () => {
		clearHide();
		if (!cellRef.current) return;
		const rect = cellRef.current.getBoundingClientRect();
		setShowMorePos({
			left: rect.right + 75,
			top: rect.top - 18 + rect.height / 2,
		});
		if (isExpanded) {
			measureShowLessPosition();
		}
		setHovered(true);
	};

	const hideButton = () => {
		clearHide();
		hideTimer.current = window.setTimeout(() => setHovered(false), 100);
	};

	const sharedButtonClass =
		"text-nowrap font-omnes-regular text-sm text-supply-blue-500 hover:text-supply-blue-300 hover:cursor-pointer underline";

	return (
		<div
			ref={cellRef}
			className={className}
			onMouseEnter={showButton}
			onMouseMove={showButton}
			onMouseLeave={hideButton}
		>
			{children}

			{needsExpansion && hovered && (
				<SupplyPortal containerId="copyable-cell-portal">
					{isExpanded ? (
						/*
						 * "Show Less" — positioned after the last character of the text
						 * via Range API measurement. translateY(-50%) vertically centers
						 * it on the last line.
						 */
						<div
							style={{
								position: "fixed",
								left: showLessPos.left,
								top: showLessPos.top,
								transform: "translateY(-50%)",
								zIndex: 9999,
								pointerEvents: "auto",
							}}
							onMouseEnter={showButton}
							onMouseLeave={hideButton}
						>
							<button
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
									setHovered(false);
									onToggle();
								}}
								className={sharedButtonClass}
							>
								Show Less
							</button>
						</div>
					) : (
						<div
							style={{
								position: "fixed",
								left: showMorePos.left,
								top: showMorePos.top,
								transform: "translate(-100%, -45%)",
								zIndex: 9999,
								pointerEvents: "auto",
							}}
							onMouseEnter={showButton}
							onMouseLeave={hideButton}
						>
							<button
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
									setHovered(false);
									onToggle();
								}}
								className={`${sharedButtonClass} mt-3`}
							>
								Show More
							</button>
						</div>
					)}
				</SupplyPortal>
			)}
		</div>
	);
};

interface ShowMoreProps extends CellProps<any> {
	originalCell: (props: CellProps<any>) => React.ReactNode;
}

const ShowMore: FC<ShowMoreProps> = ({ originalCell, value, ...cellProps }) => {
	const [isExpanded, setIsExpanded] = useState(false);

	const needsExpansion = value != null && String(value).length > 50;

	return (
		<ShowMoreWrapper
			isExpanded={isExpanded}
			onToggle={() => setIsExpanded(!isExpanded)}
			needsExpansion={needsExpansion}
			className="relative flex items-center w-full"
		>
			{originalCell({ value, ...cellProps } as CellProps<any>)}
		</ShowMoreWrapper>
	);
};

export default React.memo(ShowMore);
